import { PageHero, LocalBusinessSchema } from '@components/common';
import { CALENDLY_URL } from '@constants';
import { Shield, Target, Users, Clock, MapPin } from 'lucide-react';
import PageFAQ from '@components/sections/PageFAQ';

export default function LondonPage() {
  return (
    <>
      <LocalBusinessSchema city="London" latitude={51.5074} longitude={-0.1278} />
      <PageHero
        title="Fractional CISO Services in London"
        subtitle="Executive cybersecurity leadership for the capital's thriving tech sector"
        eyebrow="London"
        seoTitle="Fractional CISO London | The CISO Network"
        seoDescription="Expert fractional CISO services for London businesses. Access senior cybersecurity leadership without the full-time cost."
      />

      {/* Why London Businesses Need CISOs */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
                Cybersecurity Leadership for London's Tech Hub
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  London stands as Europe's leading technology capital, home to over 80,000 tech
                  businesses ranging from ambitious startups in Shoreditch to established financial
                  institutions in the City. With the highest concentration of fintech companies
                  globally and a thriving ecosystem of SaaS, healthtech, and cybersecurity firms,
                  the capital presents unique security challenges.
                </p>
                <p>
                  The regulatory landscape in London is particularly demanding. From FCA
                  requirements for financial services to GDPR compliance for businesses handling
                  European data, companies face intense scrutiny. A data breach in this competitive
                  market doesn't just mean regulatory fines - it can destroy the trust that London
                  businesses spend years building.
                </p>
                <p>
                  Yet finding senior cybersecurity talent in London's competitive market is
                  increasingly difficult. Full-time CISOs command salaries exceeding £150,000, plus
                  benefits, bonuses, and equity - costs that can strain growing businesses. This is
                  where fractional CISO services provide the perfect solution.
                </p>
                <p>
                  London's diversity is its strength and its security challenge. From fintech
                  startups in Shoreditch to asset managers in Mayfair, from healthtech in White City
                  to SaaS platforms in King's Cross, each sub-sector carries distinct regulatory
                  obligations and threat profiles. A fractional CISO who has worked across multiple
                  London industries can navigate this complexity efficiently, applying lessons
                  learned from one sector to protect another.
                </p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="p-6 rounded-xl bg-background border-l-4 border-teal">
                <div className="flex items-start gap-4">
                  <MapPin className="h-6 w-6 text-teal flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-2">
                      Your Account Manager
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Your dedicated account manager understands the capital's unique business
                      environment, from the Square Mile to Tech City, ensuring you get CISO
                      expertise tailored to your specific sector.
                    </p>
                  </div>
                </div>
              </div>
              <div className="p-6 rounded-xl bg-background border-l-4 border-teal">
                <div className="flex items-start gap-4">
                  <Clock className="h-6 w-6 text-teal flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-2">
                      Rapid Response
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Based in London, your fractional CISO can be on-site within hours when needed,
                      attending board meetings in Canary Wharf or visiting your offices across
                      Greater London.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Why London Businesses Choose Fractional CISOs
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              Access the expertise you need without the overhead of a full-time hire
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Shield,
                title: 'Regulatory Expertise',
                description:
                  'Navigate FCA, GDPR, and industry-specific regulations with confidence.',
              },
              {
                icon: Target,
                title: 'Cost Effective',
                description: 'Save costs compared to a full-time London CISO salary.',
              },
              {
                icon: Users,
                title: 'Local Network',
                description:
                  'Access our established relationships with London security vendors and advisors.',
              },
              {
                icon: Clock,
                title: 'Flexible Engagement',
                description: 'Scale up or down based on funding rounds, audits, or incidents.',
              },
            ].map((benefit, i) => (
              <div key={i} className="text-center">
                <div className="h-14 w-14 mx-auto rounded-xl bg-teal flex items-center justify-center mb-5">
                  <benefit.icon className="h-7 w-7 text-accent-foreground" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                  {benefit.title}
                </h3>
                <p className="text-muted-foreground text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Your Account Manager
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              From first contact to CISO placement, we're with you every step of the way
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              {
                step: '01',
                title: 'Discovery',
                description:
                  'Your account manager meets with you to understand your business, sector, and specific security challenges.',
              },
              {
                step: '02',
                title: 'Matching',
                description:
                  'We match you with a CISO from our network who has relevant experience in your London market sector.',
              },
              {
                step: '03',
                title: 'Onboarding',
                description:
                  'Your CISO embeds in your business, meeting your team and assessing your security posture.',
              },
              {
                step: '04',
                title: 'Ongoing Support',
                description:
                  'Your account manager remains your point of contact, ensuring the engagement delivers value.',
              },
            ].map((step, i) => (
              <div key={i} className="relative text-center">
                <div className="text-5xl font-heading font-bold text-teal/20 mb-4">{step.step}</div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                  {step.title}
                </h3>
                <p className="text-muted-foreground text-sm">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PageFAQ
        title="Fractional CISO Services in London - FAQs"
        subtitle="Common questions from London businesses"
        items={[
          {
            id: 'london-1',
            question: 'How does a fractional CISO work with businesses in London?',
            answer:
              'Our fractional CISOs work with organisations across the UK, including London. They can attend meetings on-site or work remotely, depending on what suits your business. We match you with a CISO who understands the security challenges facing fintech, SaaS, healthtech, and professional services companies.',
          },
          {
            id: 'london-2',
            question: 'What size of business benefits from a fractional CISO in London?',
            answer:
              'Fractional CISO services are ideal for growing businesses that need strategic security leadership but are not yet ready for a full-time executive hire. This includes London startups, scale-ups, and mid-sized companies that are winning larger contracts or facing regulatory requirements.',
          },
          {
            id: 'london-3',
            question: 'What industries do your CISOs cover in London?',
            answer:
              "Our network includes CISOs with experience in fintech, SaaS, healthtech, and professional services. We match you with someone who understands your sector's specific regulatory environment and threat landscape, whether you are navigating FCA requirements, NHS Digital standards, or customer security questionnaires.",
          },
          {
            id: 'london-4',
            question: 'What is the first thing a fractional CISO does when they start?',
            answer:
              'Every engagement begins with discovery. Your CISO assesses your current security posture, understands your business priorities, and develops a tailored roadmap. This typically delivers actionable insights within the first 30 days, with quick wins identified early.',
          },
          {
            id: 'london-5',
            question: 'Can a fractional CISO help with board reporting and governance?',
            answer:
              'Yes. A core part of the fractional CISO role is translating technical security risks into business language for boards and leadership teams. Our CISOs build reporting frameworks, present to boards, and establish governance structures that satisfy regulators and investors.',
          },
        ]}
      />

      {/* CTA */}
      <section className="py-24 gradient-navy cta-photo">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-foreground mb-6">
            Ready to Secure Your London Business?
          </h2>
          <p className="text-primary-foreground/60 text-lg max-w-3xl mx-auto mb-10">
            Speak with our team today and discover how a fractional CISO can transform your
            cybersecurity posture.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="btn-hero">
              Book a Call
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
